import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyAppSubscriptionKey,
  ShopifyAppSubscriptionLineItemsKey,
  ShopifyAppSubscriptionToAppUsageRecordsKey,
  ShopifyAppSubscriptionToShopKey,
  ShopifyAppUsageRecordKey,
  ShopifyAppUsageRecordToAppSubscriptionKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyAppSubscriptionKey,
  defaultFilterableApiIdentifiers: ["status", "trialDays"],
  "2022-01": {
    name: "Shopify App Subscription",
    graphQLTypename: "AppSubscription",
    fields: [
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "createdAt" } },
      },
      {
        name: "Current Period End",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "currentPeriodEnd" } },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id" } },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "name" } },
      },
      {
        name: "Return Url",
        fieldType: FieldType.URL,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "returnUrl" } },
      },
      {
        name: "Status",
        fieldType: FieldType.Enum,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "status", isShopifyEnum: true } },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: [
            { name: "PENDING" },
            { name: "ACTIVE" },
            { name: "DECLINED" },
            { name: "EXPIRED" },
            { name: "FROZEN" },
            { name: "CANCELLED" },
            { name: "ACCEPTED" },
          ],
        },
      },
      {
        name: "Test",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "test" } },
      },
      {
        name: "Trial Days",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "trialDays" } },
        configuration: { decimals: 0 },
      },
      {
        name: "Line Items",
        fieldType: FieldType.JSON,
        key: ShopifyAppSubscriptionLineItemsKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "lineItems",
            graphQLSelection: `
        id
        plan {
          pricingDetails {
            ... on AppUsagePricing {
              __typename
              balanceUsed {
                amount
                currencyCode
              }
              cappedAmount {
                amount
                currencyCode
              }
              interval
              terms
            }
            ... on AppRecurringPricing {
              __typename
              interval
              price {
                amount
                currencyCode
              }
            }
          }
        } `,
          },
        },
      },
      {
        name: "App Usage Records",
        fieldType: FieldType.HasMany,
        key: ShopifyAppSubscriptionToAppUsageRecordsKey,
        configuration: {
          relatedModelKey: ShopifyAppUsageRecordKey,
          inverseFieldKey: ShopifyAppUsageRecordToAppSubscriptionKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyAppSubscriptionToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_node_list",
      resource: "appSubscription",
    },
  },
  "2025-04": {
    syncConfig: {
      type: "graphql_list",
      resource: "allSubscriptions",
      filters: {},
    },
  },
};

export type ShopifyAppSubscriptionLineItem = {
  id: `gid://shopify/AppSubscriptionLineItem/${string}`;
  plan: {
    pricingDetails:
      | {
          __typename: "AppUsagePricing";
          terms: string;
          interval: "EVERY_30_DAYS" | "ANNUAL";
          cappedAmount: {
            amount: string;
            currencyCode: string;
          };
          balanceUsed: {
            amount: string;
            currencyCode: string;
          };
        }
      | {
          __typename: "AppRecurringPricing";
          interval: "EVERY_30_DAYS" | "ANNUAL";
          price: {
            amount: string;
            currencyCode: string;
          };
        };
  };
};

export const ShopifyAppSubscription = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
